import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarDays, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useClasses } from '../../hooks/useClasses';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
export default function BatchFormPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const { data: existingBatch, isLoading: loadingBatch } = useQuery({
        queryKey: ['batch', id],
        queryFn: () => api.get(`/api/batches/${id}`).then(r => r.data),
        enabled: isEdit,
    });
    const [formData, setFormData] = useState({ name: '', courseName: '', classId: '', capacity: 30 });
    const [synced, setSynced] = useState(false);
    const { data: classes, isLoading: classesLoading } = useClasses();
    useEffect(() => {
        if (existingBatch && !synced) {
            setFormData({
                name: existingBatch.name || '',
                courseName: existingBatch.course_name ?? existingBatch.courseName ?? '',
                classId: String(existingBatch.class_id ?? existingBatch.classId ?? ''),
                capacity: existingBatch.capacity ?? 30,
            });
            setSynced(true);
        }
    }, [existingBatch, synced]);
    const createMutation = useMutation({
        mutationFn: async (payload) => api.post('/api/batches', payload),
        onSuccess: () => {
            toast.success('Batch created');
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
            navigate('/batches');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create batch');
        },
    });
    const updateMutation = useMutation({
        mutationFn: async (payload) => api.patch(`/api/batches/${id}`, payload),
        onSuccess: () => {
            toast.success('Batch updated');
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
            navigate('/batches');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update batch');
        },
    });
    const handleSubmit = (e) => {
        e.preventDefault();
        if (isEdit) {
            updateMutation.mutate(formData);
        }
        else {
            createMutation.mutate(formData);
        }
    };
    if (isEdit && loadingBatch) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-[200px]", children: _jsx(Loader2, { className: "w-6 h-6 animate-spin text-cyan-500" }) }));
    }
    return (_jsxs("div", { className: "space-y-4 sm:space-y-6", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { variant: "ghost", size: "icon", className: "h-10 w-10", onClick: () => navigate(-1), children: _jsx(ArrowLeft, { className: "w-5 h-5" }) }), _jsxs("div", { children: [_jsxs("h1", { className: "text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2", children: [_jsx(CalendarDays, { className: "w-5 h-5 sm:w-6 sm:h-6 text-cyan-500" }), isEdit ? 'Edit Batch' : 'Add Batch'] }), _jsx("p", { className: "text-xs sm:text-sm text-light-text-muted mt-1", children: isEdit ? 'Update batch details' : 'Create a new batch' })] })] }), _jsx("div", { className: "glass-card p-4 sm:p-6 rounded-2xl", children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Batch Name" }), _jsx(Input, { type: "text", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), required: true, className: "h-10" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Course Name" }), _jsx(Input, { type: "text", value: formData.courseName, onChange: (e) => setFormData({ ...formData, courseName: e.target.value }), className: "h-10" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Class" }), _jsxs(Select, { value: formData.classId, onValueChange: (v) => setFormData({ ...formData, classId: v }), children: [_jsx(SelectTrigger, { className: "h-10 w-full", children: _jsx(SelectValue, { placeholder: "Select" }) }), _jsx(SelectContent, { children: classesLoading ? (_jsx(SelectItem, { value: "loading", disabled: true, children: "Loading..." })) : (classes?.map((opt) => (_jsx(SelectItem, { value: opt.value, children: opt.label }, opt.value)))) })] })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Capacity" }), _jsx(Input, { type: "number", value: formData.capacity, onChange: (e) => setFormData({ ...formData, capacity: parseInt(e.target.value) }), className: "h-10" })] }), _jsxs("div", { className: "flex gap-3 pt-4", children: [_jsx(Button, { type: "button", variant: "outline", className: "flex-1 h-10", onClick: () => navigate(-1), children: "Cancel" }), _jsx(Button, { type: "submit", disabled: createMutation.isPending || updateMutation.isPending, className: "flex-1 h-10", children: createMutation.isPending || updateMutation.isPending ? (_jsx(Loader2, { className: "w-4 h-4 animate-spin" })) : (isEdit ? 'Update' : 'Create') })] })] }) })] }));
}
